import React from 'react'
import {
    BoxProps,
    Divider,
    Grid,
    GridItem,
    GridProps,
    IconButtonProps,
    Link,
    Skeleton,
    Stack,
    StackProps,
    Text,
    TextProps,
    useMultiStyleConfig,
} from '@chakra-ui/react'
import {HideBelowViewport, MenuItem} from '_uiComponents'
import {useAnalytics} from '_customHooks'
import {useProducts} from '@salesforce/commerce-sdk-react'
import SearchProductsGrid from '../products-grid'
import type {ProductSuggestions, SuggestionsColumn} from '_types'

interface SearchSuggestionsProps {
    isProductListing?: boolean
    isPageLoading?: boolean
    ideas?: SuggestionsColumn
    women?: SuggestionsColumn
    men?: SuggestionsColumn
    products?: ProductSuggestions
    onProductClick?: () => void
}

/**
 * @name SearchSuggestions
 *
 * @component
 *
 * @description
 * Renders the default state for search section which contains a grid layout
 * with a list of suggestions, including ideas, women categories, men categories, and product suggestions.
 *
 * @remarks
 * When rendered inside product listing page the order of the grid item elements is different.
 *
 * @param {SearchSuggestionsProps} props - Properties object.
 * @param {boolean} [props.isProductListing = false] - Indicates if the search suggestions are rendered in PLP (empty search results).
 * @param {boolean} [props.isPageLoading=false] - Indicates if the page designer data is currently loading.
 * @param {SuggestionsColumn} [props.ideas] - Ideas column content.
 * @param {SuggestionsColumn} [props.women] - Women column content.
 * @param {SuggestionsColumn} [props.men] - Men column content.
 * @param {ProductSuggestions} [props.products] - Products column content.
 * @param {function} [props.onProductClick] - Callback for product click event.
 *
 * @returns {React.FC<SearchSuggestionsProps>} - Rendered SearchSuggestions component.
 */
const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
    isProductListing = false,
    isPageLoading = false,
    ideas,
    women,
    men,
    products,
    onProductClick,
}) => {
    const styles = useMultiStyleConfig('SearchSuggestions') as Record<string, BoxProps & StackProps & IconButtonProps & GridProps & TextProps>
    const {analyticsEventInternalSearch} = useAnalytics()

    const {data: productsData, isLoading} = useProducts(
        {
            parameters: {
                ids: products?.productIds.join(',') ?? null,
                allImages: false,
                expand: ['none'],
                c_productTileHeader: true,
            },
        },
        {
            enabled: !!products,
            keepPreviousData: true,
        },
    )

    const handleIdeaClick = (label: string) => {
        analyticsEventInternalSearch({action: 'Ideas', label: label})
    }

    const handleMenCategoryClick = (label: string) => {
        analyticsEventInternalSearch({action: 'Men', label: label})
    }

    const handleWomenCategoryClick = (label: string) => {
        analyticsEventInternalSearch({action: 'Women', label: label})
    }

    return (
        <Grid data-testid="search-suggestions" {...styles.mainGrid}>
            <GridItem colSpan={{base: 12, lg: 'auto'}} order={{base: isProductListing ? 3 : 1, lg: 1}}>
                <Grid {...styles.leftGrid}>
                    {/* Ideas column */}
                    <GridItem data-testid="suggestions-ideas" colSpan={{base: 12, lg: 'auto'}}>
                        {isPageLoading ? (
                            <SuggestionColumnSkeleton itemsCount={4} />
                        ) : (
                            ideas && (
                                <>
                                    <Text as="h2" {...styles.columnTitle}>
                                        {ideas.title.name}
                                    </Text>
                                    <Stack {...styles.columnContent}>
                                        {ideas.items.map((idea) => (
                                            <MenuItem key={idea.name} href={idea.href} variant="ideaSuggestion" onClick={() => handleIdeaClick(idea.id)}>
                                                {idea.name}
                                            </MenuItem>
                                        ))}
                                    </Stack>
                                </>
                            )
                        )}
                    </GridItem>

                    {/* Women column */}
                    <GridItem data-testid="suggestions-women" colSpan={{base: 6, lg: 'auto'}}>
                        {isPageLoading ? (
                            <SuggestionColumnSkeleton itemsCount={5} />
                        ) : (
                            women && (
                                <>
                                    <Text as="h2" {...styles.columnTitle}>
                                        <Link href={women.title.href}>{women.title.name}</Link>
                                    </Text>
                                    <Stack {...styles.columnContent}>
                                        {women.items.map((womenItem) => (
                                            <MenuItem
                                                key={womenItem.name}
                                                href={womenItem.href}
                                                variant="genderSuggestion"
                                                onClick={() => handleWomenCategoryClick(womenItem.id)}
                                                ariaLabel={`${women.title.name} - ${womenItem.name}`}
                                            >
                                                {womenItem.name}
                                            </MenuItem>
                                        ))}
                                    </Stack>
                                </>
                            )
                        )}
                    </GridItem>

                    {/* Men column */}
                    <GridItem data-testid="suggestions-men" colSpan={{base: 6, lg: 'auto'}}>
                        {isPageLoading ? (
                            <SuggestionColumnSkeleton itemsCount={5} />
                        ) : (
                            men && (
                                <>
                                    <Text as="h2" {...styles.columnTitle}>
                                        <Link href={men.title.href}>{men.title.name}</Link>
                                    </Text>
                                    <Stack {...styles.columnContent}>
                                        {men.items.map((menItem) => (
                                            <MenuItem
                                                key={menItem.name}
                                                href={menItem.href}
                                                variant="genderSuggestion"
                                                onClick={() => handleMenCategoryClick(menItem.id)}
                                                ariaLabel={`${men.title.name} - ${menItem.name}`}
                                            >
                                                {menItem.name}
                                            </MenuItem>
                                        ))}
                                    </Stack>
                                </>
                            )
                        )}
                    </GridItem>
                </Grid>
            </GridItem>

            {/* Divider column */}
            <HideBelowViewport breakpoint="lg" as={GridItem} order={{base: isProductListing ? 2 : 4, lg: 4}}>
                <Divider orientation="vertical" aria-orientation={undefined} {...styles.gridDivider} />
            </HideBelowViewport>

            {/* Suggestions column */}
            <GridItem data-testid="suggestions-product-images" colSpan={{base: 12, lg: 'auto'}} order={{base: isProductListing ? 1 : 5, lg: 5}}>
                {isPageLoading ? (
                    <Skeleton w={'220px'} h={'17px'} mb={'spacing.8'} />
                ) : (
                    <Text as="h2" {...styles.suggestionsColumnTitle}>
                        {products?.title}
                    </Text>
                )}
                <SearchProductsGrid products={productsData?.data} isLoading={isLoading || isPageLoading} onProductClick={onProductClick} />
            </GridItem>
        </Grid>
    )
}

/**
 * @name SuggestionColumnSkeleton
 *
 * @component
 *
 * @description
 * Renders a column for suggestions with skeletons for a loading state.
 *
 * @param {number} itemsCount - Number of items to show in the column content.
 *
 * @returns {JSX.Element} - Rendered SuggestionColumnSkeleton component.
 */
const SuggestionColumnSkeleton = ({itemsCount}: {itemsCount: number}): JSX.Element => {
    return (
        <>
            <Skeleton w={'85px'} h={'17px'} mb={'spacing.6'} />
            <Stack gap={'spacing.3'}>
                {Array.from({length: itemsCount}, (_, index) => (
                    <Skeleton w={'140px'} h={'17px'} key={index} />
                ))}
            </Stack>
        </>
    )
}

export default SearchSuggestions
